/** Default minimum list-column width when panels are open (px). */
export const MIN_MAIN_WIDTH_BASE = 360;

/** Minimum side-panel width (px). */
export const MIN_PANEL_WIDTH = 360;

/** Matches ``p-4`` on the workbench root. */
export const WORKBENCH_ROOT_PADDING = 16;

/** Matches the drag-handle column width. */
export const WORKBENCH_RESIZER_WIDTH = 16;

/** Matches ``px-5`` horizontal padding on the workbench main column. */
export const WORKBENCH_MAIN_PADDING_X = 40;

/** Matches ``gap-2`` on the primary toolbar row. */
export const WORKBENCH_TOOLBAR_GAP = 8;

/**
 * Minimum compressed width per Guardians filter chip.
 * Keep in sync with ``GUARDIANS_FILTER_CHIP_MIN_WIDTH_PX``.
 */
export const FILTER_CHIP_MIN_WIDTH = 52;

/** Matches ``gap-1`` between filter chips. */
export const FILTER_CHIP_GAP = 4;

/**
 * Minimum compressed width for Guardians toolbar slot / filters actions.
 * Keep in sync with ``GUARDIANS_TOOLBAR_ACTION_MIN_WIDTH_PX``.
 */
export const TOOLBAR_SLOT_BUTTON_MIN_WIDTH = 64;

/** Sum fixed toolbar segment widths with gaps between present segments. */
export function sumToolbarSegments(segmentWidths: number[]): number {
  if (segmentWidths.length === 0) return 0;
  const gaps = Math.max(0, segmentWidths.length - 1) * WORKBENCH_TOOLBAR_GAP;
  return segmentWidths.reduce((total, width) => total + width, 0) + gaps;
}

/** Flex row holding search, filters, optional slot, and settings (may be nested). */
function resolveActionsToolbarRow(actions: HTMLElement): HTMLElement {
  const slot = actions.querySelector("[data-workbench-toolbar-slot]");
  const slotRow = slot?.parentElement;
  if (slotRow instanceof HTMLElement) {
    return slotRow;
  }
  if (
    actions.children.length === 1 &&
    actions.firstElementChild instanceof HTMLElement
  ) {
    return actions.firstElementChild;
  }
  return actions;
}

/**
 * Minimum width for the Guardians toolbar actions cluster (search, filters,
 * slot, settings). Fixed controls (``[data-toolbar-fixed]``: search trigger,
 * settings) count at their current width; resizable controls
 * (``[data-toolbar-action]`` and the visible slot button) count at their
 * compressed floor so the list can be dragged narrow enough to compress them.
 */
export function measureToolbarActionsMinWidth(actions: HTMLElement): number {
  const row = resolveActionsToolbarRow(actions);
  const segments: number[] = [];

  for (const child of Array.from(row.children)) {
    if (!(child instanceof HTMLElement)) continue;

    if (child.matches("[data-workbench-toolbar-slot]")) {
      if (
        child.getAttribute("data-visible") === "true" &&
        child.querySelector("[data-toolbar-slot-button]")
      ) {
        segments.push(TOOLBAR_SLOT_BUTTON_MIN_WIDTH);
      }
      continue;
    }

    if (child.matches("[data-toolbar-action]")) {
      segments.push(TOOLBAR_SLOT_BUTTON_MIN_WIDTH);
      continue;
    }

    segments.push(child.getBoundingClientRect().width);
  }

  return sumToolbarSegments(segments);
}

/**
 * Minimum main-column width for toolbar chrome. Fixed segments (tabs, actions)
 * are measured as-is; filter chips count at their compressed minimum, not
 * full label width.
 */
export function measureMainMinWidth(main: HTMLElement): number {
  const row = main.querySelector("[data-workbench-toolbar-row]");
  if (!row) return MIN_MAIN_WIDTH_BASE;

  const segments: number[] = [];

  const tabs = row.querySelector("[data-workbench-toolbar-tabs]");
  if (tabs) {
    segments.push(tabs.getBoundingClientRect().width);
  }

  const chipCount = row.querySelectorAll("[data-filter-chip]").length;
  if (chipCount > 0) {
    segments.push(
      chipCount * FILTER_CHIP_MIN_WIDTH + Math.max(0, chipCount - 1) * FILTER_CHIP_GAP
    );
  }

  const actions = row.querySelector("[data-workbench-toolbar-actions]");
  if (actions instanceof HTMLElement) {
    segments.push(measureToolbarActionsMinWidth(actions));
  }

  return WORKBENCH_MAIN_PADDING_X + sumToolbarSegments(segments);
}

export function resolveMinMainWidth(measured: number): number {
  return Math.max(MIN_MAIN_WIDTH_BASE, Math.ceil(measured));
}

export interface ClampMainWidthOptions {
  containerInnerWidth: number;
  minMainWidth: number;
}

export function clampMainWidth(width: number, options: ClampMainWidthOptions): number {
  const maxMain = options.containerInnerWidth - MIN_PANEL_WIDTH - WORKBENCH_RESIZER_WIDTH;
  const upperBound = Math.max(options.minMainWidth, maxMain);
  return Math.max(options.minMainWidth, Math.min(width, upperBound));
}
